<script lang="ts">
import {computed, defineComponent, PropType} from '@vue/composition-api'

import OpeningHoursSchedule from '@/components/repairshop/OpeningHoursSchedule.vue'
import {inRange, isRequired} from '@/helpers/validations'
import {useI18n} from '@/i18n'
import {DealerUser} from '@/types'

interface SalesAppointmentConditions {
  daysBeforeRequest: number
  capacityPerSlot: number
  openingHours: string[]
  notificationEmail?: string
}

const MARGIN_DAYS = 45

export default defineComponent({
  name: 'SalesAppointmentSettings',
  components: {
    OpeningHoursSchedule,
  },
  props: {
    value: {
      type: Object as PropType<SalesAppointmentConditions>,
      required: true,
    },
    users: {
      type: Array as PropType<DealerUser[]>,
      default: () => [],
    },
  },
  setup(props) {
    const {t} = useI18n('honda.repair.repairshop')
    const conditions = computed(() => props.value)

    const rules = {
      marginDays: [isRequired, (value: number) => inRange(value, MARGIN_DAYS)],
      maxSimultaneous: [isRequired, (value: number) => inRange(value, 20, 1)],
    }

    const notificationEmailOptions = computed(() => {
      const options = props.users.map(user => ({
        text: `${user.email} ${user.name}`,
        name: user.name,
        value: user.email,
      }))
      // Keep the selected value (the general fallback email, or a user since
      // removed) in the list so the dropdown never renders blank.
      const current = conditions.value?.notificationEmail
      if (current && !options.some(option => option.value === current)) {
        options.unshift({text: current, name: '', value: current})
      }
      return options
    })

    return {
      conditions,
      notificationEmailOptions,
      rules,
      t,
    }
  },
})
</script>

<template>
  <div v-if="conditions">
    <m-form-row :label="t('sales-appointment-days-before-request')">
      <m-text-field
        :rules="rules.marginDays"
        type="number"
        uid="sales-appointment-days-before-request"
        id="sales-appointment-days-before-request"
        v-model="conditions.daysBeforeRequest" />
    </m-form-row>

    <m-form-row>
      <template v-slot:custom-label>
        <div>
          <p class="mb-1">{{ t('sales-appointment-max-simultaneous') }}</p>
          <small class="text--secondary d-block">
            {{ t('sales-appointment-max-simultaneous-hint') }}
          </small>
        </div>
      </template>
      <template v-slot:default>
        <m-text-field
          :rules="rules.maxSimultaneous"
          type="number"
          uid="sales-appointment-max-simultaneous"
          id="sales-appointment-max-simultaneous"
          v-model="conditions.capacityPerSlot" />
      </template>
    </m-form-row>

    <m-form-row>
      <template v-slot:custom-label>
        <div>
          <p class="mb-1">{{ t('sales-appointment-notification-email') }}</p>
          <small class="text--secondary d-block">
            {{ t('sales-appointment-notification-email-helper') }}
          </small>
        </div>
      </template>
      <template v-slot:default>
        <div class="m-input">
          <v-select
            :items="notificationEmailOptions"
            uid="sales-appointment-notification-email"
            id="sales-appointment-notification-email"
            class="append-icon-mapit"
            append-icon="fa-light fa-angle-down"
            outlined
            :attach="true"
            hide-details="auto"
            v-model="conditions.notificationEmail">
            <template v-slot:selection="{item}">
              <span class="notification-email-option"
                >{{ item.value
                }}<span v-if="item.name"
                  >&nbsp;<strong>{{ item.name }}</strong></span
                ></span
              >
            </template>
            <template v-slot:item="{item}">
              <span class="notification-email-option"
                >{{ item.value
                }}<span v-if="item.name"
                  >&nbsp;<strong>{{ item.name }}</strong></span
                ></span
              >
            </template>
          </v-select>
        </div>
      </template>
    </m-form-row>

    <m-form-row class="mt-2">
      <template v-slot:custom-label>
        <div class="align-self-start">{{ t('sales-appointment-schedule') }}</div>
      </template>
      <template v-slot:default>
        <opening-hours-schedule class="mt-2" v-model="conditions.openingHours" />
      </template>
    </m-form-row>
  </div>
</template>

<style scoped>
.notification-email-option,
::v-deep .v-select__selections .notification-email-option {
  font-family: ProximaNovaRegular !important;
  font-weight: normal;
}
.notification-email-option strong,
::v-deep .v-select__selections .notification-email-option strong {
  font-family: ProximaNovaSemibold !important;
  font-weight: normal;
}
</style>
